import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatTZS, formatDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, MapPin, Phone, Mail, Smartphone, Loader2, Clock, Home, Copy, MessageCircle } from "lucide-react";

const METHODS = [
  { value: "mpesa", label: "M-Pesa" },
  { value: "mixx_by_yas", label: "Mixx by Yas" },
  { value: "airtel_money", label: "Airtel Money" },
  { value: "crdb", label: "CRDB SimBanking" },
  { value: "nmb", label: "NMB Mkononi" },
];

export default function BookingConfirmation() {
  const { reference } = useParams();
  const [booking, setBooking] = useState(null);
  const [settings, setSettings] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [method, setMethod] = useState("mpesa");
  const [payPhone, setPayPhone] = useState("");
  const [processing, setProcessing] = useState(false);
  const [deferring, setDeferring] = useState(false);

  const load = async () => {
    try {
      const [b, s] = await Promise.all([
        api.get(`/bookings/reference/${reference}`),
        api.get("/settings"),
      ]);
      setBooking(b.data);
      setSettings(s.data);
      setPayPhone(b.data.guest_phone || "");
      if (b.data.payment_method && b.data.payment_method !== "offline") setMethod(b.data.payment_method);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, [reference]);

  const pay = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/mobile/initiate", {
        booking_id: booking.id, method, phone: payPhone || booking.guest_phone,
      });
      toast.success(data.message || `${method.toUpperCase()} push sent. Reference ${data.transaction_id}`);
      setPayOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setProcessing(false); }
  };

  const payAtReception = async () => {
    if (!confirm("Pay at reception when you arrive? Your booking stays reserved — no card or mobile money needed now.")) return;
    setDeferring(true);
    try {
      const { data } = await api.post(`/bookings/reference/${booking.reference}/pay-at-reception`);
      toast.success(data.message || "Great — see you at reception!");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeferring(false); }
  };

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--sand)]">
        <p className="font-ui text-sm text-[color:var(--muted-text)]">Loading booking…</p>
      </div>
    );
  }

  const paid = booking.payment_status === "paid";
  const isCancelled = booking.status === "cancelled";
  const deferredToReception = booking.payment_method === "offline" || booking.pay_at_reception === true;

  return (
    <div className="min-h-screen bg-[color:var(--sand)] py-16 px-4" data-testid="confirmation-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-ui text-[color:var(--deep)] hover:text-[color:var(--brand)] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to hotel
        </Link>

        <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm border border-[color:var(--brand)]/10">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${paid ? "bg-emerald-100" : (deferredToReception ? "bg-emerald-100" : "bg-amber-100")}`}>
            {paid || deferredToReception ? <CheckCircle2 className={`w-8 h-8 ${paid || deferredToReception ? "text-emerald-600" : "text-amber-600"}`} /> : <Clock className="w-8 h-8 text-amber-600" />}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[color:var(--deep)]">Karibu Baraka Inn</h1>
          <p className="font-body text-[color:var(--muted-text)] mt-3">
            {paid
              ? "Your booking is fully confirmed and paid. See you soon!"
              : deferredToReception
                ? "Booking confirmed — pay at reception on arrival. Nothing else needed from you now."
                : "Your room is reserved. Complete payment below or pay at check-in."}
          </p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Reference" value={booking.reference} testid="conf-reference" />
            <Stat label="Room type" value={booking.room_type_name || "—"} />
            <Stat label="Check-in" value={formatDate(booking.check_in_date)} />
            <Stat label="Check-out" value={formatDate(booking.check_out_date)} />
            <Stat label="Nights" value={booking.nights} />
            <Stat label="Guests" value={`${booking.adults}A · ${booking.children}C`} />
            <Stat label="Payment" value={booking.payment_method.replace("_", " ")} />
            <Stat label="Status" value={booking.payment_status} accent />
          </div>

          {(booking.add_ons || []).length > 0 || (booking.group_discount && booking.group_discount.applied) ? (
            <div className="mt-8 rounded-2xl border border-[color:var(--brand)]/15 bg-white overflow-hidden" data-testid="conf-summary">
              <div className="px-5 py-3 bg-[color:var(--sand)] border-b border-[color:var(--brand)]/10 font-ui text-xs uppercase tracking-wider text-[color:var(--deep)]">Breakdown</div>
              <div className="divide-y divide-[color:var(--brand)]/10">
                {booking.group_discount?.applied && (
                  <div className="px-5 py-3 flex items-center justify-between gap-3 text-emerald-700" data-testid="conf-discount">
                    <div className="min-w-0">
                      <p className="font-ui text-sm font-semibold">{booking.group_discount.label || "Trekker rate"} · −{booking.group_discount.percent}%</p>
                      <p className="text-xs mt-0.5">Auto-applied — {booking.group_discount.reason}</p>
                    </div>
                    <span className="font-ui text-sm font-semibold flex-shrink-0">−{formatTZS(booking.group_discount.amount || 0)}</span>
                  </div>
                )}
                {(booking.add_ons || []).map((ao, i) => (
                  <div key={i} className="px-5 py-3 flex items-start justify-between gap-3" data-testid={`conf-addon-${i}`}>
                    <div className="min-w-0">
                      <p className="font-ui text-sm font-semibold text-[color:var(--deep)]">{ao.title} × {ao.quantity}</p>
                      {ao.duration && <p className="text-xs font-ui text-[color:var(--muted-text)] mt-0.5">{ao.duration}</p>}
                      {(ao.includes || []).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {ao.includes.map((inc, j) => (
                            <span key={j} className="text-[10px] font-ui text-[color:var(--deep)] bg-[color:var(--sand)] border border-[color:var(--brand)]/15 px-1.5 py-0.5 rounded-full">✓ {inc}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="font-ui text-sm font-semibold text-[color:var(--brand)] flex-shrink-0">{formatTZS(ao.line_total)}</span>
                  </div>
                ))}
                <div className="px-5 py-3 flex items-center justify-between bg-[color:var(--sand)]/50">
                  <span className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Room {formatTZS(booking.room_total || 0)} + add-ons {formatTZS(booking.add_ons_total || 0)}</span>
                  <span className="font-heading text-base text-[color:var(--deep)]">= {formatTZS(booking.total_amount)}</span>
                </div>
              </div>
            </div>
          ) : null}

          <div className="mt-8 p-6 rounded-2xl bg-[color:var(--sand)] border border-[color:var(--brand)]/15">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <span className="font-ui text-sm text-[color:var(--deep)]">Amount due</span>
                <p className="font-heading text-3xl text-[color:var(--brand)]" data-testid="conf-amount">{formatTZS(booking.total_amount)}</p>
              </div>
              {!paid && !isCancelled && !deferredToReception && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={payAtReception} disabled={deferring} variant="outline" size="lg" data-testid="pay-reception-btn" className="rounded-full h-12 px-5 border-2 border-[color:var(--deep)] text-[color:var(--deep)] hover:bg-[color:var(--deep)] hover:text-white">
                    {deferring ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Home className="w-4 h-4 mr-2" /> Pay at Reception</>}
                  </Button>
                </div>
              )}
              {deferredToReception && !paid && (
                <span className="px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 font-ui text-sm font-semibold" data-testid="reception-badge">✓ Pay at reception on arrival</span>
              )}
              {paid && <span className="px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 font-ui text-sm font-semibold">✓ Paid</span>}
            </div>
            {!paid && !isCancelled && !deferredToReception && (
              <p className="text-xs font-ui text-[color:var(--muted-text)] mt-3">
                Choose to pay now via mobile money using the number below, <b>or</b> tap &ldquo;Pay at Reception&rdquo; and settle in cash / card when you arrive. Either way your room stays reserved.
              </p>
            )}
            {deferredToReception && !paid && (
              <p className="text-xs font-ui text-[color:var(--muted-text)] mt-3">
                We&apos;ve emailed you a confirmation. Please bring your ID on <b>{formatDate(booking.check_in_date)}</b>. Reception will settle the {formatTZS(booking.total_amount)} in cash or card on arrival.
              </p>
            )}
          </div>

          {!paid && !isCancelled && !deferredToReception && settings?.payment_number && (
            <div className="mt-6 p-6 rounded-2xl bg-white border-2 border-[color:var(--brand)]/20" data-testid="payment-instructions-card">
              <p className="font-ui uppercase tracking-[0.2em] text-[10px] text-[color:var(--brand)]">Pay by mobile money</p>
              <h3 className="font-heading text-xl text-[color:var(--deep)] mt-1">Send {formatTZS(booking.total_amount)} to</h3>
              <div className="mt-4 flex items-center gap-3 p-4 rounded-xl bg-[color:var(--sand)]">
                <div className="flex-1 min-w-0">
                  <p className="font-ui text-[11px] uppercase tracking-wider text-[color:var(--muted-text)]">Lipa Namba / number</p>
                  <p className="font-mono text-xl sm:text-2xl text-[color:var(--deep)] font-bold tracking-wider break-all" data-testid="payment-number">{settings.payment_number}</p>
                  {settings.payment_name && <p className="font-ui text-xs text-[color:var(--muted-text)] mt-1">Registered as <b className="text-[color:var(--deep)]">{settings.payment_name}</b></p>}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { navigator.clipboard?.writeText(settings.payment_number); toast.success("Number copied"); }}
                  data-testid="copy-payment-number"
                  className="border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)] hover:text-white flex-shrink-0"
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <ol className="mt-4 space-y-1.5 font-ui text-sm text-[color:var(--deep)] list-decimal list-inside">
                <li>Open M-Pesa / Mixx / Airtel Money on your phone.</li>
                <li>Choose <b>Lipa na M-Pesa &rarr; Pay Bill / Lipa Namba</b> (or send money).</li>
                <li>Enter number <b className="font-mono">{settings.payment_number}</b> and amount <b>{formatTZS(booking.total_amount)}</b>.</li>
                <li>Reference: <b className="font-mono">{booking.reference}</b>.</li>
                <li>Send us the payment SMS/screenshot via WhatsApp so reception marks it paid.</li>
              </ol>
              {settings.payment_instructions && <p className="mt-3 text-xs font-ui text-[color:var(--muted-text)] italic">{settings.payment_instructions}</p>}
              {settings.whatsapp && (
                <a
                  href={`https://wa.me/${settings.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Habari Baraka Inn. Nimelipia booking ${booking.reference} (${booking.guest_name}) — ${formatTZS(booking.total_amount)}. Nimekutumia receipt hapa 👇`)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="send-receipt-whatsapp"
                  className="mt-5 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-ui font-semibold transition-colors"
                >
                  <MessageCircle className="w-5 h-5" /> Send payment receipt on WhatsApp
                </a>
              )}
            </div>
          )}

          {settings && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm font-ui">
              <div className="flex items-start gap-2 text-[color:var(--deep)]"><MapPin className="w-4 h-4 text-[color:var(--brand)] mt-0.5" /><span>{settings.address}</span></div>
              <div className="flex items-center gap-2 text-[color:var(--deep)]"><Phone className="w-4 h-4 text-[color:var(--brand)]" /><span>{settings.phone}</span></div>
              <div className="flex items-center gap-2 text-[color:var(--deep)]"><Mail className="w-4 h-4 text-[color:var(--brand)]" /><span>{settings.email}</span></div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={payOpen && false} onOpenChange={setPayOpen}>
        <DialogContent data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Pay {formatTZS(booking.total_amount)}</DialogTitle>
            <DialogDescription>Choose a mobile money option. You&apos;ll get a push notification on your phone to approve the payment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Mobile money provider</Label>
              <RadioGroup value={method} onValueChange={setMethod} className="grid grid-cols-2 gap-2">
                {METHODS.map((m) => (
                  <label key={m.value} htmlFor={`pm-${m.value}`}
                    className={`cursor-pointer p-3 rounded-xl border-2 transition-colors flex items-center gap-2 ${method === m.value ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5" : "border-[color:var(--brand)]/15 bg-white hover:bg-[color:var(--sand)]"}`}
                    data-testid={`pm-${m.value}`}>
                    <RadioGroupItem id={`pm-${m.value}`} value={m.value} className="sr-only" />
                    <Smartphone className="w-4 h-4 text-[color:var(--brand)]" />
                    <span className="font-ui text-sm font-medium text-[color:var(--deep)]">{m.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label>Phone number to bill</Label>
              <Input value={payPhone} onChange={(e) => setPayPhone(e.target.value)} placeholder="+255 712 345 678" data-testid="pay-phone" className="mt-1" />
            </div>
            <p className="text-xs text-[color:var(--muted-text)]"><strong>MOCKED:</strong> No real charge yet. The hotel will confirm your payment on arrival.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={pay} disabled={processing} data-testid="pay-submit" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Push · {formatTZS(booking.total_amount)}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="p-4 rounded-xl bg-[color:var(--sand)] border border-[color:var(--brand)]/10">
      <p className="text-xs font-ui uppercase tracking-wider text-[color:var(--muted-text)]">{label}</p>
      <p className={`mt-1 font-ui text-sm font-semibold ${accent ? "text-[color:var(--brand)]" : "text-[color:var(--deep)]"}`} data-testid={testid}>
        {value}
      </p>
    </div>
  );
}
